import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'
import { UserRound } from 'lucide-react'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'
import { useContext, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router'

import CertificateCard from '@/components/CertificateCard/CertificateCard'
import { ConfirmButton } from '@/components/ConfirmButton'
import { Button } from '@/components/elements/button'
import { Skeleton } from '@/components/elements/skeleton'
import { Layout } from '@/components/Layout'
import StatusIcon from '@/components/StatusIcon/StatusIcon'
import EventSlideoutView from '@/pages/Accounts/Calendar/EventSlideoutInstructorView'
import { getStudentEntityCertificates } from '@/services/entity_student_certificates.service'
import { completeCourseTraining } from '@/services/student_courses.service'
import { UserStoreContext } from '@/stores/UserStore'
import { toast } from '@/utils/helpers'
import { formatUserName } from '@/utils/userHelpers'

import GrantCertificateModal from './GrantCertificateModal'
import InstructorCourseModal from './InstructorCourseModal'
import RegisterForCertificateEventModal from './RegisterForCertificateEventModal'
import StudentCourseModal from './StudentCourseModal'

const HandsOnStatus = observer(({ studentCertificate, entityId }) => {
  const { canManageInstructorResources } = useContext(UserStoreContext)
  // Training status is now at the certificate level
  /** @type {null | 'Not Started' | 'Completed'} */
  const state = studentCertificate.trainingStatus

  // Data processing
  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()

  const completionMutation = useMutation({
    mutationFn: () => completeCourseTraining(entityId, studentCertificate.id),
    onSuccess: () => {
      handleSuccess('Hands On training marked as completed')
      queryClient.invalidateQueries({ queryKey: ['accounts', 'students', 'certificates'] })
    },
    onError: (err) => {
      handleError(err.message)
    },
  })
  const markCompleted = () => {
    completionMutation.mutate()
  }
  return (
    <>
      {state === null && (
        <div className="flex items-center gap-2">
          <StatusIcon status="Not Started" />
          N/A
        </div>
      )}
      {state === 'Not Started' && (
        <div className="flex items-center gap-2">
          <StatusIcon status="In Progress" />
          {canManageInstructorResources ? (
            <ConfirmButton
              confirmContent="Are you sure you want to mark the hands on portion completed?"
              confirmTitle="Mark Completed"
              loading={completionMutation.isPending}
              onClick={markCompleted}
              variant="outline"
              size="sm"
            >
              Mark Completed
            </ConfirmButton>
          ) : (
            <span>Not Started</span>
          )}
        </div>
      )}
      {state === 'Completed' && (
        <div className="flex items-center gap-2">
          <StatusIcon status="Completed" />
          Completed
        </div>
      )}
    </>
  )
})

HandsOnStatus.propTypes = {
  studentCertificate: PropTypes.object.isRequired,
  entityId: PropTypes.string.isRequired,
}

const InstructorsStudent = observer(() => {
  // Context
  const { entityId, studentId } = useParams()
  const { canManageInstructorResources } = useContext(UserStoreContext)

  // State
  const [selectedCourse, setSelectedCourse] = useState(null)
  const [viewAsStudentCourse, setViewAsStudentCourse] = useState(null)
  const [selectedCertificateToGrant, setSelectedCertificateToGrant] = useState(null)
  const [viewEvent, setViewEvent] = useState(null)
  const [registerForCertificate, setRegisterForCertificate] = useState(null)

  // Data fetching
  const certificateQuery = useQuery({
    queryKey: ['accounts', 'students', 'certificates', entityId, studentId],
    queryFn: () => getStudentEntityCertificates({ entityId, studentId }),
  })

  const student = certificateQuery?.data?.results?.[0]?.student
  const name = student ? formatUserName(student.user) : 'Student'

  const breadcrumbs = [
    'Instructor',
    { pathname: `/accounts/${entityId}/instructors/students`, title: 'Students' },
    name,
  ]

  const { 'In Progress': inProgress = [], Issued: issued = [] } = useMemo(() => {
    if (!certificateQuery.data) return {}
    const certificates = certificateQuery.data.results.map((certificate) => ({
      ...certificate,
      courseworkCompleted: certificate.courseRecords.every((courseRecord) => {
        if (courseRecord.status !== 'Completed') return false

        if (courseRecord.trainingStatus === 'Not Started') return false
        return true
      }),
    }))
    return _.groupBy(certificates, (cert) => (cert.status === 'Issued' ? 'Issued' : 'In Progress'))
  }, [certificateQuery.data])

  if (certificateQuery.isPending) {
    return (
      <Layout breadcrumbs={breadcrumbs}>
        <Skeleton className="mb-4 h-8 w-48" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Skeleton className="h-64 w-full rounded-lg" />
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
      </Layout>
    )
  }

  const grantCertificate = (certificate) => {
    setSelectedCertificateToGrant(certificate)
  }

  const buildInProgressActions = (studentCertificate) => {
    if (!canManageInstructorResources) return []
    return [
      {
        label: 'Grant Certificate',
        onClick: () => grantCertificate(studentCertificate),
        disabled: !studentCertificate.courseworkCompleted,
      },
    ]
  }

  return (
    <Layout breadcrumbs={breadcrumbs}>
      <Layout.Heading title={name}>
        <Button asChild variant="outline" size="sm">
          <Link to={`/accounts/${entityId}/users/${studentId}`}>
            <UserRound className="mr-1 h-4 w-4" />
            View Profile
          </Link>
        </Button>
      </Layout.Heading>
      <div className="flex flex-col gap-8">
        <section>
          <h2 className="uppercase">Assigned</h2>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {inProgress.map((studentCertificate) => (
              <CertificateCard
                key={studentCertificate.id}
                studentCertificate={studentCertificate}
                renderOnline={(course) => (
                  <div className="flex items-center gap-2">
                    <StatusIcon status={course.status} />
                    {course.status === 'Not Started' ? (
                      <span className="text-nowrap">{course.status}</span>
                    ) : (
                      <div className="flex gap-1">
                        <Button
                          onClick={() => setSelectedCourse(course)}
                          size="sm"
                          variant="outline"
                        >
                          View
                        </Button>
                        {course.status === 'In Progress' && (
                          <Button
                            onClick={() => setViewAsStudentCourse(course)}
                            size="sm"
                            variant="outline"
                          >
                            Open Course
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}
                renderHandsOn={(cert) => (
                  <HandsOnStatus studentCertificate={cert} entityId={entityId} />
                )}
                onViewEvent={(event) => setViewEvent({ event, studentId })}
                onScheduleTraining={
                  canManageInstructorResources
                    ? () => setRegisterForCertificate(studentCertificate)
                    : undefined
                }
                actions={buildInProgressActions(studentCertificate)}
              />
            ))}
          </div>

          {inProgress.length === 0 && (
            <p className="text-sm text-gray-500">{name} has no courses assigned.</p>
          )}
        </section>
        <hr />
        <section>
          <h2 className="uppercase">Completed</h2>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {issued.map((studentCertificate) => (
              <CertificateCard
                key={studentCertificate.id}
                studentCertificate={studentCertificate}
                renderOnline={(course) => (
                  <div className="flex items-center gap-2">
                    <StatusIcon status={course.status} />
                    <Button onClick={() => setSelectedCourse(course)} size="sm" variant="outline">
                      View
                    </Button>
                  </div>
                )}
                renderHandsOn={(cert) =>
                  cert.trainingStatus === null ? (
                    <div className="flex items-center gap-2">
                      <StatusIcon status="Not Started" />
                      N/A
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <StatusIcon status={cert.trainingStatus} />
                      {cert.trainingStatus}
                    </div>
                  )
                }
                onViewEvent={(event) => setViewEvent({ event, studentId })}
              />
            ))}
          </div>
          {issued.length === 0 && (
            <p className="text-sm text-gray-500">{name} has not yet completed any courses.</p>
          )}
        </section>
      </div>
      {selectedCourse && (
        <StudentCourseModal
          courseRecord={selectedCourse}
          onClose={() => setSelectedCourse(null)}
          entityId={entityId}
          name={name}
        />
      )}
      {viewAsStudentCourse && (
        <InstructorCourseModal
          courseRecord={viewAsStudentCourse}
          closeModal={() => setViewAsStudentCourse(null)}
          entityId={entityId}
          studentName={name}
        />
      )}
      {selectedCertificateToGrant && (
        <GrantCertificateModal
          certificate={selectedCertificateToGrant}
          isOpen={!!selectedCertificateToGrant}
          onClose={() => setSelectedCertificateToGrant(null)}
          entityId={entityId}
          name={name}
        />
      )}
      {viewEvent && (
        <EventSlideoutView
          viewEvent={viewEvent.event}
          canManage={canManageInstructorResources}
          resetAndClose={() => {
            setViewEvent(null)
          }}
        />
      )}
      {registerForCertificate && (
        <RegisterForCertificateEventModal
          studentCertificateId={registerForCertificate.id}
          certificateId={registerForCertificate.certificate.id}
          entityId={entityId}
          name={name}
          onClose={() => {
            setRegisterForCertificate(null)
          }}
        />
      )}
    </Layout>
  )
})

export default InstructorsStudent
